import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { usePublishedPage } from './usePublishedPage';
import { usePublishedSteps } from './usePublishedSteps';
import { publishedStatusTag } from './publishedStatusTag';
import { PublishedPageKind } from './publishedPageKind';
import { splitShareUrl } from './publishedPageFacts';
import { SMLPublishedHeader } from './SMLPublishedHeader';
import { SMLPublishedAddress } from './SMLPublishedAddress';
import { SMLPublishedThumb } from './SMLPublishedThumb';
import { SMLPublishedLede } from './SMLPublishedLede';
import { SMLPublishedRoutes } from './SMLPublishedRoutes';
import { SMLPublishedShare } from './SMLPublishedShare';
import { SMLPublishedQRBox } from './SMLPublishedQRBox';
import { SMLPublishedNextSteps } from './SMLPublishedNextSteps';
import { SMLPublishedActions } from './SMLPublishedActions';
import styles from './SMLPublishedSheet.module.scss';

interface SMLPublishedSheetProps {
  accountId: number;
  smartLink: SerializedSmartLink;
  closeModal: () => void;
  openSmartLink: () => void;
}

/**
 * What the artist sees the first time a page goes live.
 *
 * The order is the order of the first minute: the address they came for, the
 * page as proof, where it sends people, the four places it goes next, and the
 * short list of things worth doing while they are still here.
 */
const SMLPublishedSheetComponent: React.FC<SMLPublishedSheetProps> = ({
  accountId,
  smartLink,
  closeModal,
  openSmartLink,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const [isQROpen, setIsQROpen] = useState(false);
  const page = usePublishedPage(accountId, smartLink);
  const steps = usePublishedSteps({ accountId, smartLinkId: smartLink.id, page, closeModal });

  const kind = useMemo(() => {
    if (page.isPresave) {
      return PublishedPageKind.presave;
    }
    return page.isBioLink ? PublishedPageKind.bioLink : PublishedPageKind.release;
  }, [page.isPresave, page.isBioLink]);

  const toggleQR = useCallback(() => setIsQROpen((wasOpen) => !wasOpen), []);

  const goToCreator = useCallback(() => {
    closeModal();
    router.push(SMLRoutes.details, { accountId, smartLinkId: smartLink.id });
  }, [closeModal, router, accountId, smartLink.id]);

  const label = useMemo(() => {
    switch (kind) {
      case PublishedPageKind.presave:
        return intl.formatMessage({
          id: 'app.sml.published.label.presave',
          defaultMessage: 'Pre-save page published',
        });
      case PublishedPageKind.bioLink:
        return intl.formatMessage({ id: 'app.sml.published.label.bio', defaultMessage: 'Bio link published' });
      case PublishedPageKind.release:
      default:
        return intl.formatMessage({
          id: 'app.sml.published.label.release',
          defaultMessage: 'Release page published',
        });
    }
  }, [intl, kind]);

  // The bio link is the one page whose name is the app's, not the artist's.
  const title =
    kind === PublishedPageKind.bioLink
      ? intl.formatMessage({ id: 'app.sml.bio-link', defaultMessage: 'Bio Link' })
      : page.title;
  const tag = publishedStatusTag(intl, kind, page.releaseDate);
  const { shareUrl } = page;
  const showRoutes = kind === PublishedPageKind.release && page.linkedServices.length > 0;

  return (
    <>
      <SMLPublishedHeader label={label} onClose={closeModal} />
      <div className={styles.body}>
        {shareUrl !== null && <SMLPublishedAddress shareUrl={shareUrl} />}

        <div className={styles.summary}>
          <SMLPublishedThumb previewUrl={page.previewUrl} coverUrl={page.coverUrl} />
          <SMLPublishedLede title={title} kind={kind} releaseDate={page.releaseDate} />
        </div>

        {showRoutes && <SMLPublishedRoutes services={page.linkedServices} onEdit={goToCreator} />}

        {shareUrl !== null && (
          <SMLPublishedShare shareUrl={shareUrl} shareTitle={title} isQROpen={isQROpen} onToggleQR={toggleQR} />
        )}

        {shareUrl !== null && isQROpen && <SMLPublishedQRBox shareUrl={shareUrl} slug={splitShareUrl(shareUrl).slug} />}

        <SMLPublishedNextSteps steps={steps} tag={tag} />

        <SMLPublishedActions onDone={closeModal} onOpenPage={openSmartLink} />
      </div>
    </>
  );
};

export const SMLPublishedSheet = TypedMemo(SMLPublishedSheetComponent);
